import { mercuryApi } from '$api'
import { KeyValueBindingModel } from '$components/common'
import {
  CompanyPreferences,
  logger,
  enqueueLoadedSnackbar,
  exceptionMessageHandler,
} from '$utils'
import { getCountryCodeFromLocale } from 'src/components/routes/_common/helpers/locale/companyLocale'
import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'
import { SalesTax } from '$models'
import { ActiveCompany, PayrollPayType } from '$models'
import { PosItem } from '../_common/interfaces/posItem'
import { PayrollSystem } from '$constants'
import { salesTaxService } from '$services'

interface CompanyPreferencesState {
  defaultPayrollPayTypes: PayrollPayType[]
  customPayrollPayTypes: PayrollPayType[]
  localeSalesTax: SalesTax[]
  companySalesTax: SalesTax[]
  posItems: PosItem[]
}

export interface CompanyPreferencesSlice extends CompanyPreferencesState {
  /**
   * Get all default and company payroll pay types
   * Populates both defaultPayrollPayTypes and customDefaultPayTypes states
   */
  getPayrollPayTypes: () => Promise<void>

  /**
   * Get the company's local sales tax (e.g. NZ or AU specific tax rates)
   * @param activeCompany the currently active company
   */
  getLocaleSalesTax: (activeCompany: ActiveCompany | null) => Promise<void>

  /**
   * Get the company's tax rates (e.g. the custom ones the company has defined)
   * @param activeCompany the currently active company
   */
  getCompanySalesTax: () => Promise<void>

  /**
   * Gets all POS items for the company
   */
  getPosItems: () => Promise<void>

  /**
   * Sets the custom payroll pay types state
   * @param updatedPayTypes the updated array of custom payroll pay types
   */
  setCustomPayrollPayTypes: (updatedPayTypes: PayrollPayType[]) => void

  /**
   * Updates the company's sales tax with new values
   * @param rates the tax rates that are being updated
   * @param errorMessage the message to display if the request fails
   * @param successMessage the message to display if the request is sucessful
   */
  updateCompanySalesTax: (
    rates: SalesTax[],
    errorMessage?: string,
    successMessage?: string
  ) => Promise<void>

  /**
   * Updated the company's preferences
   * @param preferences the preferences that are being updated
   * @param errorMessage the message to display if the request fails
   * @param successMessage the message to display if the request is sucessful
   */
  updateCompanyPreferences: (
    preferences: KeyValueBindingModel[],
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Add a new Custom Payroll Pay Type
   * @param payType the pay type to be added
   * @param errorMessage the message to display if the request fails
   * @param successMessage the message to display if the request is sucessful
   */
  addCustomPayrollPayType: (
    payType: PayrollPayType,
    errorMessage?: string,
    successMessage?: string
  ) => Promise<void>

  /**
   * Updates a Custom Payroll Pay Type
   * @param payType the updated pay type
   * @param errorMessage the message to display if the request fails
   * @param successMessage the message to display if the request is sucessful
   */
  updateCustomPayrollPayType: (
    payType: PayrollPayType,
    errorMessage?: string,
    successMessage?: string
  ) => Promise<void>

  /**
   * Delete a Custom Payroll Pay Type
   * @param payTypeRef the pay type reference to be removed
   * @param errorMessage the message to display if the request fails
   * @param successMessage the message to display if the request is sucessful
   */
  removeCustomPayrollPayType: (
    payTypeRef: string,
    errorMessage?: string,
    successMessage?: string
  ) => Promise<void>

  /**
   * Resets the CompanyPreference Slice
   */
  resetCompanyPreferencesSlice: () => void
}

const initialState: CompanyPreferencesState = {
  defaultPayrollPayTypes: [],
  customPayrollPayTypes: [],
  companySalesTax: [],
  localeSalesTax: [],
  posItems: [],
}

const createCompanyPreferencesSlice: StateCreator<
  CompanyPreferencesSlice & SharedSlice,
  [],
  [],
  CompanyPreferencesSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State Setters
  resetCompanyPreferencesSlice: () => set(initialState),

  setCustomPayrollPayTypes: (updatedPayTypes: PayrollPayType[]) =>
    set({ customPayrollPayTypes: updatedPayTypes }),

  getPayrollPayTypes: async (): Promise<void> => {
    return mercuryApi
      .get<PayrollPayType[]>('payroll/pay-type')
      .then((result) =>
        set(() => ({
          defaultPayrollPayTypes: result.filter(
            (payType) => payType.payrollSystem !== PayrollSystem.Custom
          ),
          customPayrollPayTypes: result.filter(
            (payType) => payType.payrollSystem === PayrollSystem.Custom
          ),
        }))
      )
      .catch((error) => {
        set(() => ({
          defaultPayrollPayTypes: [],
          customPayrollPayTypes: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve payroll pay types')
      })
  },

  getCompanySalesTax: async (): Promise<void> => {
    return salesTaxService
      .getAllSalesTaxRates()
      .then((result) => set(() => ({ companySalesTax: result })))
      .catch((error) => {
        set(() => ({
          companySalesTax: [],
        }))
        exceptionMessageHandler(
          error,
          "Failed to retrieve your company's sales tax"
        )
      })
  },

  getLocaleSalesTax: async (
    activeCompany: ActiveCompany | null
  ): Promise<void> => {
    const companyLocale = getCountryCodeFromLocale(
      activeCompany?.preference(CompanyPreferences.Locale) as string
    )
    const companyProvince = activeCompany?.preference(
      CompanyPreferences.Province
    )

    return salesTaxService
      .getLocaleSalesTaxRates(companyLocale, companyProvince)
      .then((result) => {
        set(() => ({ localeSalesTax: result }))
      })
      .catch((error) => {
        set(() => ({ localeSalesTax: [] }))
        exceptionMessageHandler(
          error,
          "Failed to retreive your company's locale sale tax rates"
        )
      })
  },

  getPosItems: async (): Promise<void> => {
    return mercuryApi
      .get<PosItem[]>('/pos/items')
      .then((result) => set(() => ({ posItems: result })))
      .catch((error) => {
        set(() => ({
          posItems: [],
        }))
        exceptionMessageHandler(
          error,
          "Failed to retrieve your company's POS items"
        )
      })
  },

  updateCompanySalesTax: async (
    rates: SalesTax[],
    errorMessage?: string,
    successMessage?: string
  ): Promise<void> => {
    try {
      set(() => ({ isLoading: true }))

      const updatedCompanySalesTax: SalesTax[] =
        await salesTaxService.updateSaleTaxRates(rates)

      if (!updatedCompanySalesTax) {
        logger.error(`${errorMessage} - updatedCompanySalesTax doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      set(() => ({
        companySalesTax: updatedCompanySalesTax,
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  updateCompanyPreferences: async (
    preferences: KeyValueBindingModel[],
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      const updatedPreferences: SalesTax[] = await mercuryApi.add(
        `/preferences`,
        preferences
      )

      if (!updatedPreferences) {
        logger.error(
          `${errorMessage} - updatedCompanyPreferences doesn't exist`
        )

        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  addCustomPayrollPayType: async (
    payType: PayrollPayType,
    errorMessage?: string,
    successMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      const postModel = {
        payType: payType.payType,
        payCode: payType.payCode,
      }

      // This request returns only the payTypeRef
      const newPayTypeRef = await mercuryApi.add('/payroll/pay-type', postModel)

      if (!newPayTypeRef) {
        logger.error(
          `${errorMessage} - creating a new Pay Type didn't return a payTypeRef`
        )

        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      // I know this is not the best way of 'adding' a new object to a state but as the reponse returns just the PayTypeRef
      // not the whole object so we have to map the payrollPayTypes and edit the relevant entry in the state. (Also we hold newly added pay types
      // in the state e.g. ones that have not been added to the DB yet, so this is an easy band-aid fix)
      set((state) => ({
        customPayrollPayTypes: state.customPayrollPayTypes.map(
          (currentPayType) => {
            if (currentPayType.payTypeRef == payType.payTypeRef) {
              // For some reason the paytype POST reponse is a object with one attribute "data" that holds the payTypeRef
              return {
                ...payType,
                payTypeRef: newPayTypeRef.data,
                isNew: false,
              }
            }
            return currentPayType
          }
        ),
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  updateCustomPayrollPayType: async (
    payType: PayrollPayType,
    errorMessage?: string,
    successMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      // This request returns 204 No Content
      await mercuryApi.update('/payroll/pay-type', payType)

      // I know this is not the best way of 'updating' an object in a state but as the reponse does not return anything
      // so we have to map the payrollPayTypes and edit the relevant entry in the state. (Also we hold newly added pay types
      // in the state e.g. ones that have not been added to the DB yet, so this is an easy band-aid fix)
      set((state) => ({
        customPayrollPayTypes: state.customPayrollPayTypes.map(
          (currentPayType) => {
            if (currentPayType.payTypeRef == payType.payTypeRef) {
              return { ...payType, isBeingEdited: false }
            }
            return currentPayType
          }
        ),
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  removeCustomPayrollPayType: async (
    payTypeRef: string,
    errorMessage?: string,
    successMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      // This request returns a 204 No Content
      await mercuryApi.remove(`/payroll/pay-type/${payTypeRef}`)

      set((state) => ({
        customPayrollPayTypes: state.customPayrollPayTypes.filter(
          (payType) => payType.payTypeRef !== payTypeRef
        ),
      }))

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },
})

export default createCompanyPreferencesSlice
